import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ToastProvider from './components/ui/ToastProvider';
import {
    Dashboard,
    Login,
    OTPVerification,
    Microsites,
    CreateMicrosite,
    Reports,
    TemplateCreatePage,
    StoreManagement,
    Blogs,
    CreateBlog,
    AIPurchaseNumbersOverview,
    AIPurchaseNumbersStatePage,
    AIPurchaseNumbersBrandPage,
    CentralProductMasterOverview,
    VerificationQueue,
    MasterEditor,
    ExciseCategoryCuration,
    VolumeSettings,
    ConfigTemplates,
    TemplateEditorPage,
} from './pages';
import { useAppDispatch } from './store';
import { loadAuthFromStorage } from './store/slices/authSlice';
import apiClient from './utils/api/ApiClient';

function App() {
    const dispatch = useAppDispatch();
    const [isAuthLoaded, setIsAuthLoaded] = useState(false);

    // Load auth from storage on app initialization
    useEffect(() => {
        const loadAuth = async () => {
            const result = await dispatch(loadAuthFromStorage());
            // Set auth token in apiClient if auth was loaded successfully
            if (result.payload) {
                const { token } = result.payload as { user: any; token: string };
                apiClient.setAuthToken(token);
            }
            setIsAuthLoaded(true);
        };
        loadAuth();
    }, [dispatch]);

    // Show loading state while auth is being loaded
    if (!isAuthLoaded) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e0e7ff]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e64f0]"></div>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <ToastProvider>
                <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/verify-otp" element={<OTPVerification />} />

                    {/* Protected routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <Dashboard />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/microsites"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <Microsites />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/microsites/create"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <CreateMicrosite />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/microsites/edit/:micrositeID"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <CreateMicrosite />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/reports" element={<Navigate to="/reports/catalog" replace />} />
                    <Route
                        path="/reports/templates/new"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <TemplateCreatePage />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports/templates/:id/duplicate"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <TemplateCreatePage />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports/:place"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <Reports />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/store-management"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <StoreManagement />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/blogs"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <Blogs />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/blogs/create"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <CreateBlog />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/blogs/edit/:blogId"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <CreateBlog />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ai-purchase-numbers"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <AIPurchaseNumbersOverview />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ai-purchase-numbers/state/:state"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <AIPurchaseNumbersStatePage />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ai-purchase-numbers/state/:state/brand/:brandId"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <AIPurchaseNumbersBrandPage />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/central-product-master"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <CentralProductMasterOverview />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/central-product-master/verification-queue"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <VerificationQueue />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/central-product-master/master-editor"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <MasterEditor />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/central-product-master/excise-category"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <ExciseCategoryCuration />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/central-product-master/volume-settings"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <VolumeSettings />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/config-templates"
                        element={<Navigate to="/config-templates/brand-level" replace />}
                    />
                    <Route
                        path="/config-templates/:place"
                        element={
                            <ProtectedRoute>
                                <Layout flush>
                                    <ConfigTemplates />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/config-templates/:place/:id"
                        element={
                            <ProtectedRoute>
                                <Layout flush hideHeader>
                                    <TemplateEditorPage />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />

                    {/* Default redirect */}
                    <Route
                        path="/"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <Dashboard />
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </ToastProvider>
        </BrowserRouter>
    );
}

export default App;
